import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { QuickProfileProvider } from "@/context/QuickProfileContext";
import { LanguageProvider } from "@/i18n";
import AppShell from "@/components/AppShell";
import SessionExpiredDialog from "@/components/SessionExpiredDialog";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import HomeDashboard from "@/pages/HomeDashboard";
import Employees from "@/pages/Employees";
import Payroll from "@/pages/Payroll";
import TitipBalance from "@/pages/TitipBalance";
import JaritPage from "@/pages/JaritPage";
import AccountCenter from "@/pages/AccountCenter";
import KasbonEmployee from "@/pages/KasbonEmployee";
import BankMaster from "@/pages/BankMaster";
import FingerspotIntegration from "@/pages/FingerspotIntegration";
import FingerspotAttendance from "@/pages/FingerspotAttendance";
import EndOfDayCheck from "@/pages/EndOfDayCheck";
import MonthlyAnalytics from "@/pages/MonthlyAnalytics";
import ApprovalCenter from "@/pages/ApprovalCenter";
import AttendanceCorrection from "@/pages/AttendanceCorrection";
import EmployeeProfile from "@/pages/EmployeeProfile";
import EmployeeNew from "@/pages/EmployeeNew";
import KasbonPage from "@/pages/KasbonPage";
import Settings from "@/pages/Settings";
import AttendanceDay from "@/pages/AttendanceDay";
import Absensi from "@/pages/Absensi";
import AttendancePage from "@/pages/AttendancePage";
import MyData from "@/pages/MyData";

const Loading = () => (
  <div className="grid min-h-screen place-items-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const Protected = ({ children, adminOnly }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin" && user.role !== "viewer") return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
};

const HomeSwitch = () => {
  const { user } = useAuth();
  return <HomeDashboard />;
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <QuickProfileProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <Protected>
                  <HomeSwitch />
                </Protected>
              }
            />
            <Route
              path="/payroll"
              element={
                <Protected adminOnly>
                  <Dashboard />
                </Protected>
              }
            />
            <Route
              path="/karyawan"
              element={
                <Protected>
                  <Employees />
                </Protected>
              }
            />
            <Route
              path="/kasbon"
              element={
                <Protected>
                  <KasbonPage />
                </Protected>
              }
            />
            <Route
              path="/settings"
              element={
                <Protected adminOnly>
                  <Settings />
                </Protected>
              }
            />
            <Route
              path="/attendance/day"
              element={
                <Protected adminOnly>
                  <AttendanceDay />
                </Protected>
              }
            />
            <Route
              path="/employees/new"
              element={
                <Protected>
                  <EmployeeNew />
                </Protected>
              }
            />
            <Route
              path="/employees/:id"
              element={
                <Protected>
                  <EmployeeProfile />
                </Protected>
              }
            />
            <Route
              path="/banks"
              element={
                <Protected adminOnly>
                  <BankMaster />
                </Protected>
              }
            />
            <Route
              path="/saldo-titip"
              element={
                <Protected adminOnly>
                  <TitipBalance />
                </Protected>
              }
            />
            <Route
              path="/jarit"
              element={
                <Protected>
                  <JaritPage />
                </Protected>
              }
            />
            <Route
              path="/absensi"
              element={
                <Protected>
                  <Absensi />
                </Protected>
              }
            />
            <Route
              path="/input-payroll"
              element={
                <Protected>
                  <AttendancePage />
                </Protected>
              }
            />
            <Route
              path="/account"
              element={
                <Protected>
                  <AccountCenter />
                </Protected>
              }
            />
            <Route
              path="/data-saya"
              element={
                <Protected>
                  <MyData />
                </Protected>
              }
            />
            <Route
              path="/kasbon/:empId"
              element={
                <Protected>
                  <KasbonEmployee />
                </Protected>
              }
            />
            <Route
              path="/periode/:id"
              element={
                <Protected adminOnly>
                  <Payroll />
                </Protected>
              }
            />
            <Route
              path="/integrations/fingerspot"
              element={
                <Protected adminOnly>
                  <FingerspotIntegration />
                </Protected>
              }
            />
            <Route
              path="/attendance/fingerprint"
              element={
                <Protected adminOnly>
                  <FingerspotAttendance />
                </Protected>
              }
            />
            <Route
              path="/approvals"
              element={
                <Protected adminOnly>
                  <ApprovalCenter />
                </Protected>
              }
            />
            <Route
              path="/koreksi-absensi"
              element={
                <Protected>
                  <AttendanceCorrection />
                </Protected>
              }
            />
            <Route
              path="/attendance/analytics"
              element={
                <Protected adminOnly>
                  <MonthlyAnalytics />
                </Protected>
              }
            />
            <Route
              path="/attendance/end-of-day"
              element={
                <Protected adminOnly>
                  <EndOfDayCheck />
                </Protected>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </QuickProfileProvider>
          <SessionExpiredDialog />
          <Toaster position="top-right" closeButton />
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
